import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import ReactPlayer from "react-player";
import KanziFadeTitle from "./KanziFadeTitle";
import "./KanziCelebration.css";

const memoryCards = [
  {
    image: "/KanziSlideshow/KanziAndSue.jpg",
    title: "A relationship built through trust",
    text: "Kanzi's abilities emerged from sustained social life, daily interaction, and a language-rich environment.",
  },
  {
    image: "/KanziSlideshow/KanziPointsToLexigram.jpg",
    title: "Symbols that carried meaning",
    text: "Lexigrams gave Kanzi a shared symbolic space for requests, descriptions, play, and understanding.",
  },
  {
    image: "/KanziSlideshow/KanziUnderUmbrella.jpg",
    title: "A life larger than demonstration",
    text: "Kanzi was not simply evidence in a study. He was a person known through affection, humor, attention, and choice.",
  },
];

const KanziCelebration = () => {
  useEffect(() => {
    document.title = "Celebrating Kanzi | Bonobo Hope Initiative";
  }, []);

  return (
    <main className="kanzi-page">
      <section className="kanzi-hero" aria-labelledby="kanzi-hero-title">
        <div className="kanzi-hero__content">
          <p className="kanzi-eyebrow">Celebrating Kanzi, 1980-2025</p>
          <h1 id="kanzi-hero-title">
            Kanzi's life changed what language could mean.
          </h1>
          <p className="kanzi-hero__lead">
            Kanzi helped the world see bonobos as culturally responsive,
            emotionally complex, communicative beings. This page honors his
            life, his relationships, and the research trajectory his work still
            asks us to restore.
          </p>
          <div className="kanzi-actions" aria-label="Page actions">
            <a className="kanzi-button kanzi-button--primary" href="#kanzi-video">
              Watch the memorial film
            </a>
            <Link
              className="kanzi-button kanzi-button--secondary"
              to="/keyboard"
            >
              Try the lexigram keyboard
            </Link>
          </div>
        </div>

        <div className="kanzi-hero__media" aria-label="Kanzi portrait">
          <img src="/KanziHeadshot.png" alt="Kanzi looking to the right" />
          <div className="kanzi-hero__badge">
            <strong>1980-2025</strong>
            <span>A life that widened the human imagination</span>
          </div>
        </div>
      </section>

      <section className="kanzi-continuum" aria-label="Kanzi story">
        <div className="kanzi-continuum__inner">
          <section className="kanzi-story" aria-labelledby="kanzi-story-title">
            <div className="kanzi-story__headline">
              <p className="kanzi-eyebrow">More than a memory</p>
              <h2 id="kanzi-story-title">
                Kanzi showed that language can grow inside relationship.
              </h2>
            </div>
            <div className="kanzi-story__body">
              <p>
                Kanzi became known for his comprehension of spoken English, his
                use of lexigrams, his toolmaking, his playfulness, and his
                unmistakable presence. But the deepest lesson of his life was
                not a single performance or a single test.
              </p>
              <p>
                His achievement came from an environment where human and bonobo
                lives were intertwined: shared attention, shared routines,
                symbolic tools, patience, care, and a culture that treated
                communication as a relationship rather than a trick.
              </p>
            </div>
          </section>

          <section className="kanzi-symbols" aria-label="Kanzi visual tribute">
            <div className="kanzi-symbols__image">
              <KanziFadeTitle />
            </div>
            <div className="kanzi-symbols__copy">
              <p className="kanzi-eyebrow">Bonobo sapien</p>
              <h2>The symbols remain a doorway.</h2>
              <p>
                The original lexigram work opened a serious scientific question:
                what becomes possible when apes are given a stable symbolic
                world, trusted partners, and time?
              </p>
            </div>
          </section>

          <section
            className="kanzi-video-section"
            id="kanzi-video"
            aria-labelledby="kanzi-video-title"
          >
            <div className="kanzi-video-section__copy">
              <p className="kanzi-eyebrow">Memorial video</p>
              <h2 id="kanzi-video-title">
                Highlights of Kanzi's linguistic understanding and life.
              </h2>
              <p>
                Watch for the moments of recognition, anticipation, humor, and
                joy. They are part of why Kanzi's story still carries such
                force.
              </p>
            </div>
            <div className="kanzi-video-frame">
              <ReactPlayer
                className="kanzi-player"
                url="https://bhivideos.s3.us-east-2.amazonaws.com/KanziMemories1a.mp4"
                playing
                loop
                muted
                controls
                playsinline
                width="100%"
                height="100%"
              />
            </div>
          </section>

          <section className="kanzi-memory-grid" aria-label="Kanzi memories">
            {memoryCards.map((card) => (
              <article className="kanzi-memory-card" key={card.title}>
                <img src={card.image} alt="" />
                <div>
                  <h3>{card.title}</h3>
                  <p>{card.text}</p>
                </div>
              </article>
            ))}
          </section>
        </div>
      </section>

      <section
        className="kanzi-cta"
        aria-labelledby="kanzi-cta-title"
        style={{
          backgroundImage:
            "linear-gradient(135deg, rgba(24, 75, 61, 0.96), rgba(13, 111, 112, 0.94)), url('/KanziSlideshow/KanziFloorLexigrams.jpg')",
        }}
      >
        <div>
          <p className="kanzi-eyebrow">Carry the work forward</p>
          <h2 id="kanzi-cta-title">Honor Kanzi by helping restore the research.</h2>
          <p>
            BHI is working toward the conditions that made Kanzi's communication
            possible: access, care, culture, scientific attention, and sustained
            support for bonobos and chimpanzees as communicative beings.
          </p>
        </div>
        <Link className="kanzi-button kanzi-button--light" to="/donate">
          Support BHI
        </Link>
      </section>
    </main>
  );
};

export default KanziCelebration;
